import React, { useState } from "react"
import {
    Alert, Box, Button, CircularProgress, Dialog, DialogActions,
    DialogContent, DialogTitle, IconButton, Paper, Stack, TextField, Typography
} from "@mui/material"
import { DataGrid } from "@mui/x-data-grid"
import EditIcon from "@mui/icons-material/Edit"
import DeleteIcon from "@mui/icons-material/Delete"
import AddIcon from "@mui/icons-material/Add"
import { asDialogData } from "../../../core/utils/utils"
import {
    useGetAllAppCopyQuery,
    useUpsertAppCopyMutation,
    useDeleteAppCopyMutation,
} from "../../../core/api/experience-api"
import ResponseDialog from "../../ResponseDialog"
import RichTextEditor from "../../Common/RichTextEditor"

// Suggested keys grouped by feature — pre-fill new rows from this list.
// Admins can also enter a custom key.
const SUGGESTED_KEYS = [
    { key: "experience.header", description: "Title and text above the progress bar on the My Experience page" },
]

// Admin CRUD for the generic `app_copy` collection — title + HTML body keyed by string id.
// Rows are referenced from the frontend by their key (e.g. `experience.header`).
// Body editor is the small RichTextEditor (p / h2 / h3 / a / ul / ol / b / i / u).
export default function AppCopyAdmin() {
    const { data: rows = [], isLoading } = useGetAllAppCopyQuery()
    const [upsert, upsertResp] = useUpsertAppCopyMutation()
    const [deleteCopy] = useDeleteAppCopyMutation()

    const [editing, setEditing] = useState(null)
    const [form, setForm] = useState({ key: "", title: "", body: "" })
    const [respOpen, setRespOpen] = useState(false)
    const [actionResp, setActionResp] = useState({})

    function openNew() {
        setEditing({})
        setForm({ key: "", title: "", body: "" })
    }
    function openEdit(row) {
        setEditing(row)
        setForm({ key: row.key, title: row.title || "", body: row.body || "" })
    }
    function close() { setEditing(null) }

    async function save() {
        const resp = await upsert({
            key: form.key.trim(),
            title: form.title,
            body: form.body,
        })
        setActionResp(asDialogData(resp))
        setRespOpen(true)
        if (!resp.error) close()
    }

    async function remove(row) {
        if (!window.confirm(`Delete copy «${row.key}»?`)) return
        const resp = await deleteCopy(row.key)
        setActionResp(asDialogData(resp))
        setRespOpen(true)
    }

    const columns = [
        { field: "key", headerName: "Key", width: 240 },
        { field: "title", headerName: "Title", width: 240 },
        { field: "body", headerName: "Body (preview)", flex: 1, renderCell: (p) => {
            const text = (p.value || "").replace(/<[^>]*>/g, "").trim()
            return text.length > 80 ? text.slice(0, 80) + "…" : text
        }},
        { field: "updatedAt", headerName: "Updated", width: 170, renderCell: (p) => p.value || "—" },
        { field: "actions", headerName: "", width: 100, renderCell: (p) => (
            <Stack direction="row">
                <IconButton size="small" onClick={() => openEdit(p.row)}>
                    <EditIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => remove(p.row)}>
                    <DeleteIcon fontSize="small" />
                </IconButton>
            </Stack>
        )},
    ]

    if (isLoading) return <Box sx={{ py: 4, textAlign: "center" }}><CircularProgress /></Box>

    const tableRows = rows.map(r => ({ ...r, id: r.key }))

    return (
        <Box sx={{ py: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h5">Texts</Typography>
                <Button variant="contained" color="warning" startIcon={<AddIcon />} onClick={openNew}>
                    New text
                </Button>
            </Stack>

            <Alert severity="info" sx={{ mb: 2 }}>
                Generic copy editor. Each text is identified by a key
                (e.g. <code>experience.header</code>) and rendered by the frontend
                in the right place. Body supports HTML — headings, links, lists.
                <br />Hint: the <code>experience.header</code> key is shown on the «My Experience» page above the progress bar.
            </Alert>

            <Paper sx={{ height: 540 }}>
                <DataGrid
                    rows={tableRows}
                    columns={columns}
                    pageSize={25}
                    rowsPerPageOptions={[10, 25, 50]}
                    disableRowSelectionOnClick
                />
            </Paper>

            <Dialog open={!!editing} onClose={close} maxWidth="md" fullWidth>
                <DialogTitle>{editing?.id ? "Edit text" : "New text"}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField
                            label="Key"
                            helperText={editing?.id
                                ? "Identifier — read-only after creation"
                                : "Stable identifier referenced by the frontend (e.g. experience.header)"}
                            value={form.key}
                            onChange={e => setForm({ ...form, key: e.target.value })}
                            disabled={!!editing?.id}
                            required
                        />
                        {!editing?.id && SUGGESTED_KEYS.length > 0 && (
                            <Alert severity="info" sx={{ py: 0.5 }}>
                                <Typography sx={{ fontSize: 12, fontWeight: 600, mb: 0.5 }}>Suggested keys:</Typography>
                                <Stack spacing={0.5}>
                                    {SUGGESTED_KEYS.map(s => (
                                        <Box key={s.key}>
                                            <Button
                                                size="small"
                                                onClick={() => setForm({ ...form, key: s.key })}
                                                sx={{ textTransform: "none", p: 0.25, minHeight: 0 }}
                                            >
                                                <code>{s.key}</code>
                                            </Button>
                                            <Typography component="span" sx={{ ml: 1, fontSize: 11, color: "text.secondary" }}>
                                                — {s.description}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Stack>
                            </Alert>
                        )}
                        <TextField
                            label="Title"
                            value={form.title}
                            onChange={e => setForm({ ...form, title: e.target.value })}
                        />
                        <Box>
                            <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 0.5 }}>Body</Typography>
                            <RichTextEditor
                                value={form.body}
                                onChange={(html) => setForm({ ...form, body: html })}
                                placeholder="Type the body text here…"
                                minHeight={220}
                            />
                        </Box>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={close}>Cancel</Button>
                    <Button variant="contained" color="warning" onClick={save} disabled={upsertResp.isLoading}>
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            <ResponseDialog
                open={respOpen}
                onClose={() => setRespOpen(false)}
                responseData={actionResp}
                label="Text"
                information="Saved"
            />
        </Box>
    )
}
